import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Typography from "@mui/material/Typography";

import { captureHandledException } from "src/services/sentry";

import { bulkUploadCertificate } from "../../api/certificates";
import { getComplianceWithCourses, ComplianceWithCoursesResponseHydrated } from "../../api/compliance";
import { getCrewUsers } from "../../api/crew";
import { BulkCertificateUpload } from "../../components/CertificateUpload";
import Spinner from "../../components/Spinner";
import type { UserCrew } from "../../shared/api-types/user";

export interface UploadableCourse {
  key: string;
  id: number;
  name: string;
  isCustom: boolean;
  userIds: number[];
  validityPeriodMonths: number | null;
}

export interface RoleGroup {
  id: number;
  name: string;
  userIds: number[];
}

export interface DerivedFormData {
  courses: UploadableCourse[];
  roles: RoleGroup[];
  users: UserCrew[];
}

export function deriveFormData(
  batch: Record<string, ComplianceWithCoursesResponseHydrated>,
  users: UserCrew[],
): DerivedFormData {
  const courseMap = new Map<string, UploadableCourse>();
  // roleId → userIds who have at least one uploadable course in that role
  const roleMap = new Map<number, RoleGroup>();
  // userId → set of course keys they have uploadable
  const userUploadableCourses = new Map<number, Set<string>>();

  for (const [userIdStr, compliance] of Object.entries(batch)) {
    const userId = Number(userIdStr);
    const { certificateMap, relationTree } = compliance;

    for (const [key, cert] of Object.entries(certificateMap)) {
      if (cert?.actions?.canUploadCertificate) {
        if (!courseMap.has(key)) {
          const isCustom = key.startsWith("cc_");
          const numericId = isCustom ? Number(key.replace("cc_", "")) : Number(key);
          courseMap.set(key, {
            key,
            id: numericId,
            name: cert.name ?? key,
            isCustom,
            userIds: [],
            validityPeriodMonths: cert.validPeriod ?? null,
          });
        }
        courseMap.get(key)!.userIds.push(userId);

        if (!userUploadableCourses.has(userId)) {
          userUploadableCourses.set(userId, new Set());
        }
        userUploadableCourses.get(userId)!.add(key);
      }
    }

    // Only include this user in a role group if they have at least one uploadable course
    if (userUploadableCourses.has(userId)) {
      for (const role of relationTree.roles) {
        if (!roleMap.has(role.id)) {
          roleMap.set(role.id, { id: role.id, name: role.name, userIds: [] });
        }
        if (!roleMap.get(role.id)!.userIds.includes(userId)) {
          roleMap.get(role.id)!.userIds.push(userId);
        }
      }
    }
  }

  const relevantUserIds = new Set(userUploadableCourses.keys());
  const presentUsers = users.filter((u) => relevantUserIds.has(u.id));

  return {
    courses: Array.from(courseMap.values()).sort((a, b) => {
      if (a.isCustom !== b.isCustom) return a.isCustom ? 1 : -1;
      return a.name.localeCompare(b.name);
    }),
    roles: Array.from(roleMap.values()).sort((a, b) => a.name.localeCompare(b.name)),
    users: presentUsers.sort((a, b) => a.fullname.localeCompare(b.fullname)),
  };
}

interface BulkCertificateUploadPageProps {
  match: { params: { crewId: string } };
}

const BulkCertificateUploadPage = ({ match }: BulkCertificateUploadPageProps) => {
  const { t } = useTranslation();
  const crewId = Number(match.params.crewId);

  const [formData, setFormData] = useState<DerivedFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!crewId) return;
    setLoading(true);
    Promise.all([getComplianceWithCourses(crewId), getCrewUsers(crewId)])
      .then(([complianceResponse, users]) => {
        setFormData(deriveFormData(complianceResponse.data, users));
      })
      .catch((err) => {
        captureHandledException(err, { feature: "bulkCertificateUpload", action: "load", crewId });
        setError(t("certificate_upload.bulk.load_error"));
      })
      .finally(() => setLoading(false));
  }, [crewId]);

  const onSubmit = async (values: Parameters<typeof bulkUploadCertificate>[0]): Promise<string | null> => {
    try {
      const result = await bulkUploadCertificate(values);
      if (result?.status === "ok") {
        return null;
      }
      return result?.reason ?? t("certificate_upload.error_generic");
    } catch (err) {
      captureHandledException(err, { feature: "bulkCertificateUpload", action: "submit", crewId });
      return t("certificate_upload.error_generic");
    }
  };

  return (
    <>
      <Typography variant="h4">{t("certificate_upload.bulk.title")}</Typography>
      {loading && <Spinner />}
      {error && (
        <Typography
          color="error"
          sx={{
            mt: 2,
          }}
        >
          {error}
        </Typography>
      )}
      {!loading && !error && formData && (
        <BulkCertificateUpload
          crewId={crewId}
          courses={formData.courses}
          roles={formData.roles}
          users={formData.users}
          onSubmit={onSubmit}
        />
      )}
    </>
  );
};

export default BulkCertificateUploadPage;
